/**
 * ui/thumbnail-service.js
 * Hàng đợi + lập lịch tạo thumbnail cho thẻ Customizer.
 *
 * Mỗi thumbnail đi qua các bước, MỖI BƯỚC nằm trong một requestIdleCallback riêng (fallback setTimeout)
 * và đều được bọc performance.measure để số đo phản ánh đúng phần việc chạy trên main thread:
 *   vas-thumb-probe   kiểm tra WebGL bằng canvas thử (một lần)
 *   vas-thumb-init    khởi tạo renderer, chia INIT_STEPS bước (mỗi bước một entry): context + scene, avatar nền,
 *                     upload texture + compile shader + vẽ thử (khi renderer chưa sống)
 *   vas-thumb-config  dựng/cấu hình avatar mẫu (vẽ canvas texture, tạo avatar preset)
 *   vas-thumb-compile compileAsync: biên dịch shader song song (phần đồng bộ nhỏ)
 *   vas-thumb-render  renderer.render()
 *   vas-thumb-encode  chụp bitmap + convertToBlob (hoặc toBlob/toDataURL ở nhánh dự phòng)
 *   vas-thumb-apply   xử lý kết quả: ghi cache, tạo object URL, gán src
 *   vas-thumb-cache   gán ảnh lấy từ cache IndexedDB
 *   vas-thumb-dispose giải phóng renderer sau 5 giây hàng đợi rỗng
 * Thẻ đã có trong cache được hiển thị ngay mà không render lại.
 */

import { ThumbnailRenderer, INIT_STEPS, measured } from './thumbnail-renderer.js';
import { thumbKey, getCachedThumb, setCachedThumb } from './thumbnail-cache.js';

export { THUMB_VERSION } from './thumbnail-cache.js';

const IDLE_TIMEOUT_MS = 1000;
const ITEM_GAP_MS = 16;
export const DISPOSE_DELAY_MS = 5000;

function requestIdle(cb) {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => cb(), { timeout: IDLE_TIMEOUT_MS });
  } else {
    setTimeout(cb, 1);
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export class ThumbnailService {
  constructor(renderer = new ThumbnailRenderer()) {
    this.renderer = renderer;
    this.queue = [];
    this.running = false;
    this.lookups = 0;
    this.writes = 0;
    this.renderedCount = 0;
    this.cachedCount = 0;
    this.failedCount = 0;
    this.unsupported = false;
    this.disposeTimer = null;
    /** img -> object URL đang giữ, để thu hồi khi ảnh tải xong hoặc khi bị thay src. */
    this.objectUrls = new WeakMap();
  }

  /** Chạy fn trong một idle callback riêng, có đo thời gian. Trả về Promise của kết quả. */
  #stage(name, fn, detail) {
    return new Promise((resolve, reject) => {
      requestIdle(() => {
        try {
          resolve(measured(name, fn, detail));
        } catch (err) {
          reject(err);
        }
      });
    });
  }

  /**
   * Yêu cầu thumbnail cho một thẻ. Có trong cache thì hiển thị ngay, không thì xếp hàng render.
   * @param {'outfit'|'hair'|'preset'} kind
   */
  async request(kind, value, imgEl, cardEl) {
    // Luôn thử cache trước: ảnh đã lưu từ phiên trước dùng được dù phiên này không có WebGL
    this.lookups++;
    let cached = null;
    try {
      cached = await getCachedThumb(thumbKey(kind, value));
    } catch {
      cached = null;
    }
    if (cached) {
      measured('vas-thumb-cache', () => {
        this.cachedCount++;
        this.applyImage(imgEl, cardEl, cached);
      });
      this.lookups--;
      return;
    }
    if (!this.unsupported) {
      this.queue.push({ kind, value, imgEl, cardEl });
      this.#pump();
    }
    this.lookups--;
  }

  async #pump() {
    if (this.running) return;
    this.running = true;
    this.#clearDisposeTimer();
    try {
      while (this.queue.length > 0 && !this.unsupported) {
        const item = this.queue.shift();
        try {
          await this.#process(item);
        } catch {
          this.failedCount++;
          // Context mất hoặc lỗi bất thường: bỏ thumbnail này (emoji vẫn hiển thị), thumbnail sau dựng lại renderer
          if (!this.renderer.alive) this.renderer.dispose();
        }
        await sleep(ITEM_GAP_MS);
      }
    } finally {
      this.running = false;
      if (this.unsupported) this.queue.length = 0;
      this.#armDispose();
    }
  }

  async #process(item) {
    const r = this.renderer;
    if (!r.alive) {
      if (r.supported === null) await this.#stage('vas-thumb-probe', () => r.probe());
      let ok = r.supported === true;
      for (let step = 0; ok && step < INIT_STEPS; step++) {
        ok = await this.#stage('vas-thumb-init', () => r.initStep(step));
      }
      if (!ok) {
        this.unsupported = true;
        return;
      }
    }
    const detail = `${item.kind}:${item.value}`;
    await this.#stage('vas-thumb-config', () => r.configure(item.kind, item.value), detail);
    await this.#stage('vas-thumb-compile', () => r.compile(), detail);
    await this.#stage('vas-thumb-render', () => r.render(), detail);
    const data = await this.#stage('vas-thumb-encode', () => r.encode(), detail);
    if (!data) {
      this.failedCount++;
      return;
    }
    measured('vas-thumb-apply', () => {
      this.renderedCount++;
      this.writes++;
      setCachedThumb(thumbKey(item.kind, item.value), data).finally(() => this.writes--);
      this.applyImage(item.imgEl, item.cardEl, data);
    });
  }

  /** Gán ảnh (Blob hoặc dataURL) vào img; thu hồi object URL ngay khi ảnh tải xong hoặc lỗi. */
  applyImage(imgEl, cardEl, data) {
    if (!imgEl) return;
    const previous = this.objectUrls.get(imgEl);
    if (previous) {
      URL.revokeObjectURL(previous);
      this.objectUrls.delete(imgEl);
    }

    const isBlob = typeof Blob !== 'undefined' && data instanceof Blob;
    const src = isBlob ? URL.createObjectURL(data) : data;
    const release = () => {
      if (isBlob && this.objectUrls.get(imgEl) === src) {
        URL.revokeObjectURL(src);
        this.objectUrls.delete(imgEl);
      }
    };
    if (isBlob) this.objectUrls.set(imgEl, src);

    imgEl.onload = () => {
      release();
      if (cardEl) cardEl.classList.add('thumb-loaded');
    };
    imgEl.onerror = () => {
      release();
      if (cardEl) cardEl.classList.remove('thumb-loaded');
    };
    imgEl.src = src;
  }

  #clearDisposeTimer() {
    if (this.disposeTimer) {
      clearTimeout(this.disposeTimer);
      this.disposeTimer = null;
    }
  }

  /** Hàng đợi rỗng quá DISPOSE_DELAY_MS thì giải phóng renderer (tạo lại khi cần). */
  #armDispose() {
    this.#clearDisposeTimer();
    if (this.queue.length > 0) return;
    this.disposeTimer = setTimeout(() => {
      this.disposeTimer = null;
      if (this.running || this.queue.length > 0) return;
      measured('vas-thumb-dispose', () => this.renderer.dispose());
    }, DISPOSE_DELAY_MS);
  }

  getStats() {
    return {
      rendered: this.renderedCount,
      cached: this.cachedCount,
      failed: this.failedCount,
      alive: this.renderer.alive,
      contexts: this.renderer.alive ? 1 : 0,
      unsupported: this.unsupported,
      queueLength: this.queue.length,
      isIdle: this.queue.length === 0 && !this.running && this.lookups === 0 && this.writes === 0
    };
  }
}

export const thumbnailService = new ThumbnailService();

if (typeof window !== 'undefined') {
  window.__thumbStats = () => thumbnailService.getStats();
}
